import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { QrCode, TrendUp, Users, SignOut, Plus, CreditCard, ChartLine } from '@phosphor-icons/react';
import MerchantAnalytics from '../../components/MerchantAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MerchantDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { language } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [sales, setSales] = useState([]);
  const [showQR, setShowQR] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const t = {
    fr: {
      overview: 'Aperçu',
      sales: 'Ventes',
      analytics: 'Analyses',
      qrCode: 'QR Code',
      settings: 'Paramètres',
      logout: 'Déconnexion',
      issueCard: 'Émettre une carte',
      viewSales: 'Voir les ventes',
      totalCustomers: 'Clients total',
      repeatRate: 'Taux de fidélité',
      weeklyChange: 'Variation hebdo',
      thisWeek: 'Cette semaine',
      lastWeek: 'Semaine dernière',
    },
    en: {
      overview: 'Overview',
      sales: 'Gift Card Sales',
      analytics: 'Analytics',
      qrCode: 'QR Code',
      settings: 'Settings',
      logout: 'Logout',
      issueCard: 'Issue Gift Card',
      viewSales: 'View All Sales',
      totalCustomers: 'Total Customers',
      repeatRate: 'Repeat Rate',
      weeklyChange: 'Weekly Change',
      thisWeek: 'This week',
      lastWeek: 'Last week',
    }
  }[language];

  // Issue gift card form
  const [issueForm, setIssueForm] = useState({
    amount: '',
    recipient_email: '',
    message: '',
    delivery_method: 'digital'
  });

  // Settings form
  const [settings, setSettings] = useState({
    accept_network_gift_cards: true,
    auto_issue_gift_card_as_reward: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, metricsRes, salesRes] = await Promise.all([
        axios.get(`${API_URL}/api/merchant/profile`, { withCredentials: true }),
        axios.get(`${API_URL}/api/merchant/metrics`, { withCredentials: true }),
        axios.get(`${API_URL}/api/merchant/sales`, { withCredentials: true })
      ]);
      setProfile(profileRes.data);
      setMetrics(metricsRes.data);
      setSales(salesRes.data.sales || []);
      
      // Load settings
      if (profileRes.data.gift_card_settings) {
        setSettings(profileRes.data.gift_card_settings);
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleIssueGiftCard = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(
        `${API_URL}/api/merchant/issue-gift-card`,
        issueForm,
        { withCredentials: true }
      );
      toast.success(`Gift card issued! Net received: £${data.net_received.toFixed(2)}`);
      setShowIssueModal(false);
      setIssueForm({ amount: '', recipient_email: '', message: '', delivery_method: 'digital' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue gift card');
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await axios.put(
        `${API_URL}/api/merchant/settings`,
        settings,
        { withCredentials: true }
      );
      toast.success('Settings updated successfully');
      setShowSettingsModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#18261E]">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-gray-300 border-r-transparent"></div>
          <p className="mt-4 text-[#889990]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#18261E]">
      {/* Header */}
      <div className="bg-[#283D32]/80 backdrop-blur-xl border-b border-[#3E5248] px-6 py-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="brand-logo text-2xl text-gray-100" data-testid="brand-logo">habitué</div>
            <h1 className="text-2xl tracking-tight text-white mt-2" data-testid="business-name">{profile?.business_name}</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowSettingsModal(true)}
              className="text-[#889990] hover:text-white transition-colors"
              data-testid="settings-button"
            >
              <span className="text-sm">Settings</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-[#889990] hover:text-white transition-colors"
              data-testid="logout-button"
            >
              <SignOut size={20} />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-6 mt-6 border-b border-[#3E5248]">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === 'overview'
                ? 'text-white border-b-2 border-gray-300'
                : 'text-[#889990] hover:text-gray-200'
            }`}
            data-testid="tab-overview"
          >
            {t.overview}
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`pb-3 text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'analytics'
                ? 'text-white border-b-2 border-[#C3A365]'
                : 'text-[#889990] hover:text-gray-200'
            }`}
            data-testid="tab-analytics"
          >
            <ChartLine size={16} />
            {t.analytics}
          </button>
          <button
            onClick={() => setActiveTab('sales')}
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === 'sales'
                ? 'text-white border-b-2 border-gray-300'
                : 'text-[#889990] hover:text-gray-200'
            }`}
            data-testid="tab-sales"
          >
            {t.sales}
          </button>
          <button
            onClick={() => setActiveTab('qr')}
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === 'qr'
                ? 'text-white border-b-2 border-gray-300'
                : 'text-[#889990] hover:text-gray-200'
            }`}
            data-testid="tab-qr"
          >
            {t.qrCode}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <button
                onClick={() => setShowIssueModal(true)}
                className="bg-[#C3A365] text-[#18261E] hover:bg-white px-6 py-4 rounded-2xl font-medium flex items-center justify-center gap-3 transition-all hover:-translate-y-1"
                data-testid="issue-gift-card-button"
              >
                <Plus size={24} weight="bold" />
                Issue Gift Card
              </button>
              <button
                onClick={() => setActiveTab('sales')}
                className="bg-[#283D32]/50 backdrop-blur-sm border border-[#3E5248] text-white hover:border-[#3E5248] px-6 py-4 rounded-2xl font-medium flex items-center justify-center gap-3 transition-all"
                data-testid="view-sales-button"
              >
                <CreditCard size={24} />
                View All Sales
              </button>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#283D32]/50 backdrop-blur-sm border border-[#3E5248] rounded-2xl p-6" data-testid="metric-customers">
                <div className="flex items-center gap-3 mb-3">
                  <Users size={24} className="text-[#E8EBE9]" />
                  <h3 className="text-sm tracking-[0.2em] uppercase text-[#889990] font-medium">{t.totalCustomers}</h3>
                </div>
                <p className="text-3xl font-semibold text-white" data-testid="total-customers">{metrics?.total_customers || 0}</p>
              </div>

              <div className="bg-[#283D32]/50 backdrop-blur-sm border border-[#3E5248] rounded-2xl p-6" data-testid="metric-repeat-rate">
                <div className="flex items-center gap-3 mb-3">
                  <TrendUp size={24} className="text-[#E8EBE9]" />
                  <h3 className="text-sm tracking-[0.2em] uppercase text-[#889990] font-medium">{t.repeatRate}</h3>
                </div>
                <p className="text-3xl font-semibold text-white" data-testid="repeat-rate">{metrics?.repeat_rate || 0}%</p>
              </div>

              <div className="bg-[#283D32]/50 backdrop-blur-sm border border-[#3E5248] rounded-2xl p-6" data-testid="metric-weekly-change">
                <div className="flex items-center gap-3 mb-3">
                  <QrCode size={24} className="text-[#E8EBE9]" />
                  <h3 className="text-sm tracking-[0.2em] uppercase text-[#889990] font-medium">{t.weeklyChange}</h3>
                </div>
                <p className={`text-3xl font-semibold ${
                  (metrics?.weekly_change || 0) >= 0 ? 'text-[#C3A365]' : 'text-red-400'
                }`} data-testid="weekly-change">
                  {metrics?.weekly_change >= 0 ? '+' : ''}{metrics?.weekly_change || 0}%
                </p>
                <p className="text-xs text-[#889990] mt-2">
                  {t.thisWeek}: {metrics?.this_week_visits || 0} | {t.lastWeek}: {metrics?.last_week_visits || 0}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <MerchantAnalytics merchantId={profile?.id} />
        )}

        {/* Sales Tab */}
        {activeTab === 'sales' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl tracking-tight text-white">Gift Card Sales</h2>
              <button
                onClick={() => setShowIssueModal(true)}
                className="bg-[#C3A365] text-[#18261E] hover:bg-white px-6 py-3 rounded-xl font-medium flex items-center gap-2"
                data-testid="issue-new-card-button"
              >
                <Plus size={20} />
                Issue New Card
              </button>
            </div>

            <div className="bg-[#283D32]/50 backdrop-blur-sm border border-[#3E5248] rounded-2xl overflow-hidden" data-testid="sales-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#283D32] border-b border-[#3E5248]">
                    <tr>
                      <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Code</th>
                      <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Amount</th>
                      <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Balance</th>
                      <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Status</th>
                      <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Recipient</th>
                      <th className="text-left px-6 py-4 text-xs tracking-[0.2em] uppercase text-[#889990] font-medium">Commission</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {sales.map((sale, index) => (
                      <tr key={sale.id} className="hover:bg-[#344840]/50 transition-colors" data-testid={`sale-row-${index}`}>
                        <td className="px-6 py-4 text-gray-200 font-mono text-sm">{sale.code?.substring(0, 12)}...</td>
                        <td className="px-6 py-4 text-white">£{sale.amount?.toFixed(2)}</td>
                        <td className="px-6 py-4 text-gray-100 font-medium">£{sale.balance?.toFixed(2)}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            sale.status === 'active'
                              ? 'bg-[#283D32]/50 text-[#889990] border border-[#3E5248]'
                              : sale.status === 'used'
                              ? 'bg-[#344840]/50 text-[#E8EBE9] border border-[#3E5248]'
                              : 'bg-yellow-900/50 text-yellow-300 border border-yellow-700'
                          }`}>
                            {sale.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-[#889990] text-sm">{sale.recipient_email || sale.recipient_phone || '-'}</td>
                        <td className="px-6 py-4 text-[#889990]">£{sale.commission?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {sales.length === 0 && (
                  <div className="text-center py-12 text-[#889990]" data-testid="no-sales">
                    No gift cards issued yet
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* QR Code Tab */}
        {activeTab === 'qr' && profile?.qr_code && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white p-8 rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.3)] text-center" data-testid="qr-code-container">
              <h2 className="text-2xl tracking-tight text-[#111827] mb-4">Customer Enrollment QR</h2>
              <p className="text-[#C3A365] mb-6">Customers scan this to join your loyalty program</p>
              <img src={profile.qr_code} alt="Merchant QR Code" className="w-64 h-64 mx-auto" data-testid="qr-code-image" />
              <p className="text-sm text-[#C3A365] mt-6">Print and display at your location</p>
            </div>
          </div>
        )}
      </div>

      {/* Issue Gift Card Modal */}
      {showIssueModal && (
        <div className="fixed inset-0 bg-[#18261E]/80 backdrop-blur-sm flex items-center justify-center z-50 px-6" onClick={() => setShowIssueModal(false)}>
          <div className="bg-gradient-to-br from-[#052e16] to-[#1F2937] border border-[#3E5248] rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="issue-modal">
            <h3 className="text-2xl tracking-tight text-white mb-6">Issue Gift Card</h3>
            <form onSubmit={handleIssueGiftCard} className="space-y-4">
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-[#889990] font-medium mb-2">Amount (£)</label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  value={issueForm.amount}
                  onChange={(e) => setIssueForm({...issueForm, amount: e.target.value})}
                  required
                  className="w-full bg-[#283D32]/50 border border-[#3E5248] rounded-xl px-4 py-3 text-white outline-none focus:ring-2 focus:ring-gray-500 placeholder:text-[#C3A365]"
                  placeholder="50.00"
                  data-testid="issue-amount-input"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-[#889990] font-medium mb-2">Recipient Email (Optional)</label>
                <input
                  type="email"
                  value={issueForm.recipient_email}
                  onChange={(e) => setIssueForm({...issueForm, recipient_email: e.target.value})}
                  className="w-full bg-[#283D32]/50 border border-[#3E5248] rounded-xl px-4 py-3 text-white outline-none focus:ring-2 focus:ring-gray-500 placeholder:text-[#C3A365]"
                  placeholder="customer@example.com"
                  data-testid="issue-email-input"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-[#889990] font-medium mb-2">Message (Optional)</label>
                <textarea
                  value={issueForm.message}
                  onChange={(e) => setIssueForm({...issueForm, message: e.target.value})}
                  rows={3}
                  className="w-full bg-[#283D32]/50 border border-[#3E5248] rounded-xl px-4 py-3 text-white outline-none focus:ring-2 focus:ring-gray-500 resize-none placeholder:text-[#C3A365]"
                  placeholder="Thank you for your purchase!"
                  data-testid="issue-message-input"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowIssueModal(false)}
                  className="flex-1 bg-[#283D32]/50 text-white border border-[#3E5248] px-6 py-3 rounded-xl font-medium hover:bg-[#344840]/50 transition-colors"
                  data-testid="issue-cancel-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-[#C3A365] text-[#18261E] hover:bg-white px-6 py-3 rounded-xl font-medium transition-colors"
                  data-testid="issue-confirm-button"
                >
                  Issue Card
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-[#18261E]/80 backdrop-blur-sm flex items-center justify-center z-50 px-6" onClick={() => setShowSettingsModal(false)}>
          <div className="bg-gradient-to-br from-[#052e16] to-[#1F2937] border border-[#3E5248] rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="settings-modal">
            <h3 className="text-2xl tracking-tight text-white mb-6">Gift Card Settings</h3>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">Accept Network Gift Cards</p>
                  <p className="text-xs text-[#889990] mt-1">Accept gift cards from other habitué merchants</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, accept_network_gift_cards: !settings.accept_network_gift_cards})}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    settings.accept_network_gift_cards ? 'bg-gray-300' : 'bg-[#344840]'
                  }`}
                  data-testid="toggle-accept-network"
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    settings.accept_network_gift_cards ? 'translate-x-6' : 'translate-x-1'
                  }`}></div>
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">Auto-issue as Reward</p>
                  <p className="text-xs text-[#889990] mt-1">Automatically issue gift card when customer earns reward</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, auto_issue_gift_card_as_reward: !settings.auto_issue_gift_card_as_reward})}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    settings.auto_issue_gift_card_as_reward ? 'bg-gray-300' : 'bg-[#344840]'
                  }`}
                  data-testid="toggle-auto-issue"
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    settings.auto_issue_gift_card_as_reward ? 'translate-x-6' : 'translate-x-1'
                  }`}></div>
                </button>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="flex-1 bg-[#283D32]/50 text-white border border-[#3E5248] px-6 py-3 rounded-xl font-medium hover:bg-[#344840]/50 transition-colors"
                data-testid="settings-cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateSettings}
                className="flex-1 bg-[#C3A365] text-[#18261E] hover:bg-white px-6 py-3 rounded-xl font-medium transition-colors"
                data-testid="settings-save-button"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantDashboard;
